import { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import usePwaInstallStatus, {
	forgetPwaInstalled,
	hasPwaInstallDismissed,
	installPromptKey,
	isIOSDevice,
	rememberPwaInstallDismissed,
} from "../hooks/usePwaInstallStatus";

const iphoneInstallHint = "Open this page in Safari, tap Share, then Add to Home Screen.";

export default function AppInstallPrompt() {
	const [isOpen, setIsOpen] = useState(false);
	const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
	const { isAppInstalled, markInstalled, setIsAppInstalled } = usePwaInstallStatus();
	const [isInstallDismissed, setIsInstallDismissed] = useState(hasPwaInstallDismissed);
	const [installHint, setInstallHint] = useState("");

	useEffect(() => {
		if (window[installPromptKey]) {
			setDeferredInstallPrompt(window[installPromptKey]);
		}

		const handleBeforeInstallPrompt = (event) => {
			event.preventDefault();
			forgetPwaInstalled();
			window[installPromptKey] = event;
			setDeferredInstallPrompt(event);
			setIsAppInstalled(false);
			setInstallHint("");
		};

		const handleAppInstalled = () => {
			window[installPromptKey] = null;
			setDeferredInstallPrompt(null);
			markInstalled();
			setIsOpen(false);
			setInstallHint("PalladiumWallet is installed.");
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
		window.addEventListener("appinstalled", handleAppInstalled);

		return () => {
			window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
			window.removeEventListener("appinstalled", handleAppInstalled);
		};
	}, [markInstalled, setIsAppInstalled]);

	const openInstallPopup = () => {
		setIsOpen(true);

		if (isAppInstalled) {
			setInstallHint("PalladiumWallet is already installed.");
			return;
		}

		setInstallHint(isIOSDevice() ? "Open this page in Safari, tap Share, then Add to Home Screen." : "Tap Install app to open the browser install request.");
	};

	const closeInstallPopup = () => {
		rememberPwaInstallDismissed();
		setIsInstallDismissed(true);
		setIsOpen(false);
		setInstallHint("");
	};

	const handleInstallApp = async () => {
		if (isAppInstalled) {
			setInstallHint("PalladiumWallet is already installed.");
			return;
		}

		const promptEvent = deferredInstallPrompt || window[installPromptKey];

		if (promptEvent) {
			try {
				await promptEvent.prompt();
				const choice = await promptEvent.userChoice;

				window[installPromptKey] = null;
				setDeferredInstallPrompt(null);
				if (choice.outcome === "accepted") {
					setInstallHint("");
				} else {
					rememberPwaInstallDismissed();
					setIsInstallDismissed(true);
					setIsOpen(false);
					setInstallHint("");
				}
			} catch (promptError) {
				console.error("PWA install prompt failed:", promptError);
				window[installPromptKey] = null;
				setDeferredInstallPrompt(null);
				setInstallHint("Use the browser install option to add PalladiumWallet to this device.");
			}

			return;
		}

		setInstallHint(isIOSDevice() ? "Open this page in Safari, tap Share, then Add to Home Screen." : "Use the browser install option to add PalladiumWallet to this device.");
	};

	const handleIphoneInstall = (event) => {
		event?.preventDefault();
		event?.stopPropagation();
		setInstallHint(iphoneInstallHint);
	};

	if (isAppInstalled || isInstallDismissed) {
		return null;
	}

	return (
		<>
			<button type="button" className="dashboard-install-trigger" onClick={openInstallPopup}>
				<AccountBalanceWalletIcon className="dashboard-install-trigger__icon" />
				<span>Download app</span>
			</button>

			{isOpen && (
				<div className="dashboard-install-overlay" role="dialog" aria-modal="true" aria-labelledby="dashboard-install-title">
					<div className="dashboard-install-popup" aria-label="Download PalladiumWallet app">
						<button type="button" className="dashboard-install-close" onClick={closeInstallPopup} aria-label="Close install popup">
							&times;
						</button>
						<div className="dashboard-install-copy">
							<span className="dashboard-install-eyebrow">Mobile app</span>
							<Typography id="dashboard-install-title" className="dashboard-install-title">Download PalladiumWallet app</Typography>
							<Typography className="dashboard-install-description">Add the app to this device for faster access to your wallet.</Typography>
						</div>
						<div className="dashboard-install-actions">
							<button type="button" className="dashboard-install-button dashboard-install-button--primary" onClick={handleInstallApp}>
								<AndroidIcon className="dashboard-install-button__icon" />
								<span className="dashboard-install-button__label">
									<span className="dashboard-install-button__platform">Android</span>
									<span className="dashboard-install-button__meta">{isAppInstalled ? "Installed" : "Install app"}</span>
								</span>
							</button>
							<button type="button" className="dashboard-install-button" onClick={handleIphoneInstall}>
								<AppleIcon className="dashboard-install-button__icon" />
								<span className="dashboard-install-button__label">
									<span className="dashboard-install-button__platform">iPhone</span>
									<span className="dashboard-install-button__meta">{isAppInstalled ? "Installed" : "Install app"}</span>
								</span>
							</button>
						</div>
						{installHint && (
							<Typography className={`dashboard-install-hint${installHint === iphoneInstallHint ? " dashboard-install-hint--alert" : ""}`}>
								{installHint}
							</Typography>
						)}
					</div>
				</div>
			)}
		</>
	);
}
